'use client'
import { useRouter } from 'next/navigation'
import { useLanguage } from '@/lib/context/LanguageContext'
import { useAlternateLinks } from '@/lib/context/AlternateLinksContext'
import { IconGlobe } from '@/components/ui/Icon'

const LANGS = ['en', 'fa'] as const

// Fixed-position language switcher. `dir="ltr"` + physical `right-*` keep it
// pinned to the same visual corner and in the same EN→FA order in both
// languages, so it never jumps when the document direction flips.
// Always visible — team feedback (Nasser, 2026-07): switching must stay
// available all the way down the page, not just on the hero.
export default function LangToggle() {
  const { lang, setLang, setLangPreference } = useLanguage()
  const { alternates } = useAlternateLinks()
  const router = useRouter()

  // Homepage: shallow URL swap, translations are in the client bundle.
  // Blog pages (alternates registered): REAL navigation to the counterpart
  // URL — blog content is server-rendered per language, and slugs differ.
  const onSelect = (code: (typeof LANGS)[number]) => {
    if (alternates) {
      setLangPreference(code)
      router.push(alternates[code] ?? (code === 'fa' ? '/fa/blog' : '/blog'))
    } else {
      setLang(code)
    }
  }

  return (
    <div
      dir="ltr"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex items-center gap-1 rounded-full bg-white p-1 pl-2.5 shadow-lg ring-1 ring-ink/10"
    >
      <span className="text-muted" aria-hidden>
        <IconGlobe className="w-3.5 h-3.5" />
      </span>
      {LANGS.map((code) => {
        const active = lang === code
        return (
          <button
            key={code}
            onClick={() => onSelect(code)}
            aria-pressed={active}
            aria-label={code === 'en' ? 'English' : 'فارسی'}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase transition-all duration-200 ${
              active ? 'bg-primary text-white' : 'text-muted hover:text-ink hover:bg-ink/5'
            }`}
          >
            {code}
          </button>
        )
      })}
    </div>
  )
}
